/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLoginByFaceIndex } from "@/api/clientApi/login";
import { message } from "@/components/Base/Message/Message.tsx";
import { toast } from "@/components/Base/Toaster/Toaster";
import { DialogType } from "@/components/Dialogs/types";
import { useDialog } from "@/components/Dialogs/useDialog";
import {
  ErrorNumberEnum,
  IgnoreErrorCode,
  NetErrorCode,
  NetErrorCodeSpecial,
} from "@/enum/request";
import { t } from "@/i18n";
import { rsaEncrypt } from "@/lib/aesUtils";
import { clearToken, getToken } from "@/lib/apiUtils";
import { DuplicatePreventionManager } from "@/lib/generalUtils";
import { routerUtils } from "@/lib/routerUtils";
import { clearBonusCenterStore } from "@/stores/bonusCenterStore";
import { KYCStore, kYCState } from "@/stores/kycStore";
import { clearUserStore } from "@/stores/userStore";
import {
  KYCVerifyStatusResult,
  UserKycAuthActionType,
} from "@/types/compontent/base/baseEnum";

import { loginFormData } from "./login/useLogin";
import { baseInfo } from "./register/useRegister";
import { usePolling } from "./useSimplePolling";
import { useWatch } from "./useWatch";

const errorToastManager = new DuplicatePreventionManager<number | string>(5000);

// import { getPointSymbolOrName } from "@/lib/pointUtils";

// 错误码常量分组
const ERROR_CODES_WITH_SYMBOL_AND_VALUE = [
  1229, 1219, 1220, 1221, 1222, 3283, 3284, 10003, 10006, 10007,
];
const ERROR_CODES_WITH_TIME_RANGE = [1206];
const ERROR_CODES_WITH_RECHARGE_TIMES = [1211];
const ERROR_CODES_WITH_SINGLE_TIME = [1209, 2302];
const ERROR_CODES_WITH_SINGLE_VALUE = [1325, 2303, 2304, 2305, 2382, 3304];

// 普通报错码
export const getNormalErrorCodes = () => {
  return NetErrorCode.filter((item) => !NetErrorCodeSpecial.includes(item));
};

// 处理普通报错
export const handleNormalToast = (errorCode: number, errStr?: string) => {
  if (!errorToastManager.shouldExecute(errorCode)) {
    return; // 5秒内已显示过相同错误码，跳过
  }
  const langCode = t(`tops.error_${errorCode}`);
  const message = langCode.startsWith("tops.error_")
    ? `${t("toast.unknown_error")} ${errorCode}`
    : `${langCode}-[${errorCode}]-[${errStr}]`;
  toast.error(message);
};

// 处理特殊报错
export const handleSpecialToast = (
  errorCode: number,
  params: string[] | null = null,
) => {
  if (!errorToastManager.shouldExecute(errorCode)) {
    return; // 5秒内已显示过相同错误码，跳过
  }
  const getSymbolOrName = (index: number) => {
    const value = params?.[index];
    return Number(value) ? "R$" : (value ?? "");
  };

  let message: string;

  if (ERROR_CODES_WITH_SYMBOL_AND_VALUE.includes(errorCode)) {
    message = t(`tops.error_${errorCode}`, {
      symbol: getSymbolOrName(1),
      value: params?.[0] ?? "",
    });
  } else if (ERROR_CODES_WITH_TIME_RANGE.includes(errorCode)) {
    message = t(`tops.error_${errorCode}`, {
      time1: params?.[0] ?? "",
      time2: params?.[1] ?? "",
    });
  } else if (ERROR_CODES_WITH_RECHARGE_TIMES.includes(errorCode)) {
    message = t(`tops.error_${errorCode}`, {
      value: params?.[1] ?? "",
    });
  } else if (ERROR_CODES_WITH_SINGLE_TIME.includes(errorCode)) {
    message = t(`tops.error_${errorCode}`, {
      time: params?.[0] ?? "",
    });
  } else if (ERROR_CODES_WITH_SINGLE_VALUE.includes(errorCode)) {
    message = t(`tops.error_${errorCode}`, {
      value: params?.[0] ?? "",
    });
  } else {
    message = t(`tops.error_${errorCode}`);
  }
  toast.error(`${message}-[${errorCode}]`);
};

const closeKycDialog = () => {
  const { closeDialog } = useDialog();
  closeDialog(DialogType.FacialRecognition);
  closeDialog(DialogType.KycIframe);
};
// 监听kyc状态
const listenKycStatus = () => {
  useWatch(
    () => KYCStore.kycInfo?.status,
    () => {
      if (KYCStore.kycInfo?.status == KYCVerifyStatusResult.success) {
        pollingStop();
        message.show(t("kyc.success"));
        closeKycDialog();
        useLoginByFaceIndex(kYCState.tokenConfig?.action_id || "");
      } else if (KYCStore.kycInfo?.status == KYCVerifyStatusResult.fail) {
        pollingStop();
        message.show(t("kyc.auth_failed"));
        closeKycDialog();
      }
    },
  );
};
// 轮询kyc状态
const POLLING_INTERVAL = 3000;
const POLLING_DURATION = 900000;
const { start: pollingStart, stop: pollingStop } = usePolling({
  interval: POLLING_INTERVAL,
  duration: POLLING_DURATION,
  callback: async () => await KYCStore.getKycStatusCpfResetPwd(),
});
const gettipList = (errorCode: number) => {
  switch (errorCode) {
    case 1350:
      return [
        t(`tops.error_${errorCode}`, {
          email: baseInfo.email ?? "",
        }),
      ];
    case 1352:
      return [
        t(`tops.error_${errorCode}`, {
          number: baseInfo.phone ?? "",
        }),
      ];
    case 1353:
      return [
        t(`tops.error_${errorCode}`, {
          cpf: baseInfo.cpf ?? "",
        }),
      ];
    case 1354:
      return [t(`tops.error_${errorCode}`)];
    default:
      return [];
  }
};

const handleIgnoreError = async (errorCode: number) => {
  const { showDialog } = useDialog();
  // const { toDeposit } = useRouter();
  switch (errorCode) {
    case 1607:
      showDialog({
        type: DialogType.Tips,
        data: {
          title: t("common.tips"),
          content: t(`tops.error_${errorCode}`),
          confirmText: t("common.deposit"),
          onConfirm: () => {
            // toDeposit({
            //   dialogCallback: () => {},
            // });
            routerUtils.push("/deposit");
          },
          // cancelText: t("common.cancel"),
        },
        zIndex: 9999,
      });
      break;
    case 2002:
    case 2003:
    case 2005:
    case 2006:
    case 2007:
      message.show(t(`tops.error_${errorCode}`));
      break;
    case 1348:
    case 1349:
    case 1351:
      {
        showDialog({
          type: DialogType.Tips,
          data: {
            title: t("common.tips"),
            content: t(`tops.error_${errorCode}`),
            confirmText: t("auth.sign_in"),
            onConfirm: () => {
              routerUtils.replace("/login");
            },
            cancelText: t("common.cancel"),
          },
          zIndex: 9999,
        });
      }
      break;
    case 1350:
    case 1352:
    case 1353:
    case 1354:
      {
        const isLogin = window?.location?.pathname?.includes("/login") || false;
        const tipList = gettipList(errorCode);
        //71644161176
        showDialog({
          type: DialogType.FacialRecognition,
          data: {
            checkIdentityCallback: async () => {
              KYCStore.toVerifyKyc(
                UserKycAuthActionType.loginByFaceIndex,
                {
                  cpf: isLogin ? loginFormData.cpf : baseInfo.cpf,
                  email: isLogin
                    ? loginFormData.email
                    : await rsaEncrypt(baseInfo.email),
                  phone: isLogin
                    ? loginFormData.phone
                    : await rsaEncrypt(baseInfo.phone),
                  a_code: isLogin ? loginFormData.a_code : baseInfo.a_code,
                },
                () => {
                  pollingStart();
                  listenKycStatus();
                },
              );
            },
            //面部辨识后需要执行回调
            callback: () => {},
            tipList: tipList,
          },
        });
      }

      break;
    default:
      break;
  }
};

// 处理错误
export function handleError(error: any) {
  try {
    // axios 错误
    if (error?.code) {
      // 网络错误/超时
      if (
        error?.code === "ERR_NETWORK" ||
        error?.code === "ECONNABORTED" ||
        error?.code === "ETIMEDOUT"
      ) {
        if (!errorToastManager.shouldExecute("network_error")) {
          return; // 5秒内已显示过相同错误码，跳过
        }
        toast.error(t("tops.network_error"));
      } else {
        if (!errorToastManager.shouldExecute(error?.code)) {
          return; // 5秒内已显示过相同错误码，跳过
        }
        toast.error(error?.code);
      }
      return;
    }

    // 安全地使用 i18n，避免在服务端渲染时出现问题
    const errCode = error._errno ?? 0;
    const errStr = error._errstr ?? "";
    if (IgnoreErrorCode.includes(errCode)) {
      // 不处理 或者 确认框等特殊处理
      handleIgnoreError(errCode);
    } else if (NetErrorCodeSpecial.includes(errCode)) {
      handleSpecialToast(errCode, error._param as string[] | null);
    } else {
      // 清除登陆缓存
      if (
        errCode == ErrorNumberEnum.TokenError ||
        errCode == ErrorNumberEnum.TokenErrorDel
      ) {
        const token = getToken();
        if (token) {
          clearToken();
          clearUserStore();
          clearBonusCenterStore();
          routerUtils.push("/login");
          handleNormalToast(errCode);
        }
      } else {
        handleNormalToast(errCode, errStr);
      }
    }
  } catch (error) {
    console.error("Failed to handle error:", error);
  }
}
